import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AppState } from '~/reducers';
import type { WebCommand, WebCommandEnum } from '../models/commands';
import { clearWebCommand } from '../store/reducer';

type Handler<T extends WebCommandEnum> = (command: WebCommand<T>) => void;

interface Options {
  enabled?: boolean;
}

/**
 * Consume the head of the webCommands FIFO queue when its `name` matches.
 *
 * When the type matches and `enabled` is true, the handler runs and the head
 * is shifted off the queue unconditionally. Use `enabled` for external
 * preconditions (e.g. an assetId not yet in the URL) — the head waits in
 * place until enabled flips true. Malformed payloads should be handled
 * defensively inside the handler; the queue still drains so later commands
 * aren't blocked.
 */
export function useConsumeWebCommand<T extends WebCommandEnum>(
  name: T,
  handler: Handler<T>,
  { enabled = true }: Options = {}
) {
  const head = useSelector((state: AppState) => state.native.webCommands?.[0]);
  const dispatch = useDispatch();
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    if (!enabled || head?.name !== name) {
      return;
    }
    handlerRef.current(head as WebCommand<T>);
    dispatch(clearWebCommand());
  }, [dispatch, enabled, head, name]);
}
